'use strict';

var xchainCrypto = require('@xchainjs/xchain-crypto');
var axios = require('axios');

function _interopDefault (e) { return e && e.__esModule ? e : { default: e }; }

var axios__default = /*#__PURE__*/_interopDefault(axios);

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * Enumeration of network types.
 */
exports.Network = void 0;
(function (Network) {
    Network["Mainnet"] = "mainnet";
    Network["Stagenet"] = "stagenet";
    Network["Testnet"] = "testnet";
})(exports.Network || (exports.Network = {}));
/**
 * Enumeration of transaction types.
 */
exports.TxType = void 0;
(function (TxType) {
    TxType["Transfer"] = "transfer";
    TxType["Unknown"] = "unknown";
})(exports.TxType || (exports.TxType = {}));
/**
 * Enumeration of fee options.
 */
exports.FeeOption = void 0;
(function (FeeOption) {
    FeeOption["Average"] = "average";
    FeeOption["Fast"] = "fast";
    FeeOption["Fastest"] = "fastest";
})(exports.FeeOption || (exports.FeeOption = {}));
/**
 * Enumeration of fee types.
 */
exports.FeeType = void 0;
(function (FeeType) {
    FeeType["FlatFee"] = "base";
    FeeType["PerByte"] = "byte";
})(exports.FeeType || (exports.FeeType = {}));

const MAINNET_THORNODE_API_BASE = 'https://thornode.ninerealms.com/thorchain';
const STAGENET_THORNODE_API_BASE = 'https://stagenet-thornode.ninerealms.com/thorchain';
const TESTNET_THORNODE_API_BASE = 'https://testnet.thornode.thorchain.info/thorchain';
class BaseXChainClient {
    /**
     * Constructor for the BaseXChainClient class.
     * Initializes the client with the provided chain and parameters.
     * @param {Chain} chain The blockchain chain identifier
     * @param {XChainClientParams} params The client parameters, including network, fee bounds, and root derivation paths
     * @throws {"Invalid phrase"} Thrown if an invalid mnemonic phrase is provided
     */
    constructor(chain, params) {
        this.phrase = ''; // The mnemonic phrase used for wallet generation
        // Initialize class properties
        this.chain = chain;
        this.network = params.network || exports.Network.Testnet;
        this.feeBounds = params.feeBounds || { lower: 1, upper: Infinity };
        // Warn if using Stagenet for real assets
        if (this.network === exports.Network.Stagenet)
            console.warn('WARNING: This is using stagenet! Real assets are being used!');
        if (params.rootDerivationPaths)
            this.rootDerivationPaths = params.rootDerivationPaths;
        // Set the mnemonic phrase if provided
        if (params.phrase) {
            if (!xchainCrypto.validatePhrase(params.phrase)) {
                throw new Error('Invalid phrase');
            }
            this.phrase = params.phrase;
        }
    }
    /**
     * Set or update the current network.
     * @param {Network} network The network to set
     * @returns {void}
     * @throws {"Network must be provided"} Thrown if no network is provided
     */
    setNetwork(network) {
        if (!network) {
            throw new Error('Network must be provided');
        }
        this.network = network;
        // Warn if using Stagenet for real assets
        if (this.network === exports.Network.Stagenet)
            console.warn('WARNING: This is using stagenet! Real assets are being used!');
    }
    /**
     * Get the current network.
     * @returns {Network} The current network
     */
    getNetwork() {
        return this.network;
    }
    /**
     * Get the fee rate from the Thorchain API.
     * @returns {Promise<FeeRate>} The fee rate
     */
    getFeeRateFromThorchain() {
        return __awaiter(this, void 0, void 0, function* () {
            const respData = yield this.thornodeAPIGet('/inbound_addresses');
            if (!Array.isArray(respData))
                throw new Error('bad response from Thornode API');
            const chainData = respData.find((elem) => elem.chain === this.chain && typeof elem.gas_rate === 'string');
            if (!chainData)
                throw new Error(`Thornode API /inbound_addresses does not contain fees for ${this.chain}`);
            return Number(chainData.gas_rate);
        });
    }
    /**
     * Make a GET request to the Thorchain API.
     * @param {string} endpoint The API endpoint
     * @returns {Promise<unknown>} The response data
     */
    thornodeAPIGet(endpoint) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = (() => {
                switch (this.network) {
                    case exports.Network.Mainnet:
                        return MAINNET_THORNODE_API_BASE;
                    case exports.Network.Stagenet:
                        return STAGENET_THORNODE_API_BASE;
                    case exports.Network.Testnet:
                        return TESTNET_THORNODE_API_BASE;
                }
            })();
            return (yield axios__default.default.get(url + endpoint)).data;
        });
    }
    /**
     * Set or update the mnemonic phrase.
     * @param {string} phrase The new mnemonic phrase
     * @param {number} walletIndex (Optional) The HD wallet index
     * @returns {Address} The address derived from the provided phrase
     * @throws {"Invalid phrase"} Thrown if an invalid mnemonic phrase is provided
     */
    setPhrase(phrase, walletIndex = 0) {
        if (this.phrase !== phrase) {
            if (!xchainCrypto.validatePhrase(phrase)) {
                throw new Error('Invalid phrase');
            }
            this.phrase = phrase;
        }
        return this.getAddress(walletIndex);
    }
    /**
     * Get the full derivation path based on the wallet index.
     * @param {number} walletIndex The HD wallet index
     * @returns {string} The full derivation path
     */
    getFullDerivationPath(walletIndex) {
        return this.rootDerivationPaths ? `${this.rootDerivationPaths[this.network]}${walletIndex}` : '';
    }
    /**
     * Purge the client by clearing the mnemonic phrase.
     * @returns {void}
     */
    purgeClient() {
        this.phrase = '';
    }
}

/**
 * singleFeeRate function generates fee rates object with a single rate for all fee options.
 *
 * @param {FeeRate} rate The fee rate to be applied to all fee options.
 * @returns {FeeRates} The fee rates object with the provided rate for all fee options.
 */
function singleFeeRate(rate) {
    return Object.values(exports.FeeOption).reduce((a, x) => ((a[x] = rate), a), {});
}
/**
 * standardFeeRates function generates standard fee rates object based on a base rate.
 *
 * @param {FeeRate} rate The base fee rate to be applied to fee options.
 * @returns {FeeRates} The fee rates object with different rates for each fee option.
 */
function standardFeeRates(rate) {
    return Object.assign(Object.assign({}, singleFeeRate(rate)), { [exports.FeeOption.Average]: rate * 0.5, [exports.FeeOption.Fastest]: rate * 5.0 });
}
/**
 * checkFeeBounds function checks if the given fee rate falls within predetermined bounds.
 * Throws an error if the fee rate is outside the bounds.
 *
 * @param {FeeBounds} feeBounds The predetermined fee rate bounds.
 * @param {FeeRate} feeRate The fee rate to be checked.
 * @throws {Error} Thrown if the fee rate is outside the predetermined bounds.
 */
function checkFeeBounds(feeBounds, feeRate) {
    if (feeRate < feeBounds.lower || feeRate > feeBounds.upper) {
        throw Error(`Fee outside of predetermined bounds: ${feeRate.toString()}`);
    }
}

/**
 * singleFee function generates a fees object with a single fee amount for all fee options.
 *
 * @param {FeeType} feeType The type of fee.
 * @param {Fee} amount The fee amount to be applied to all fee options.
 * @returns {Fees} The fees object with the provided fee amount for all fee options.
 */
function singleFee(feeType, amount) {
    return Object.values(exports.FeeOption).reduce((a, x) => ((a[x] = amount), a), {
        type: feeType,
    });
}
/**
 * standardFees function generates standard fees object based on a base fee amount.
 *
 * @param {FeeType} feeType The type of fee.
 * @param {Fee} amount The base fee amount to be applied to fee options.
 * @returns {Fees} The fees object with different fee amounts for each fee option.
 */
function standardFees(feeType, amount) {
    return Object.assign(Object.assign({}, singleFee(feeType, amount)), { [exports.FeeOption.Average]: amount.times(0.5), [exports.FeeOption.Fastest]: amount.times(5.0) });
}

/**
 * ExplorerProvider class is responsible for generating URLs for blockchain explorers.
 * It constructs explorer URLs and replaces placeholders with specific addresses or transaction IDs.
 */
class ExplorerProvider {
    /**
     * Constructor for ExplorerProvider class.
     *
     * @param {string} explorerUrl The base URL of the blockchain explorer
     * @param {string} explorerAddressUrlTemplate The template URL for address exploration
     * @param {string} explorerTxUrlTemplate The template URL for transaction exploration
     */
    constructor(explorerUrl, explorerAddressUrlTemplate, explorerTxUrlTemplate) {
        this.explorerUrl = explorerUrl; // Initialize explorerUrl property
        this.explorerAddressUrlTemplate = explorerAddressUrlTemplate; // Initialize explorerAddressUrlTemplate property
        this.explorerTxUrlTemplate = explorerTxUrlTemplate; // Initialize explorerTxUrlTemplate property
    }
    /**
     * Get the base URL of the blockchain explorer.
     *
     * @returns {string} The base URL of the blockchain explorer
     */
    getExplorerUrl() {
        return this.explorerUrl; // Return the explorerUrl property
    }
    /**
     * Get the URL for exploring a specific address.
     *
     * @param {Address} address The address to be explored
     * @returns {string} The URL for exploring the specified address
     */
    getExplorerAddressUrl(address) {
        return this.explorerAddressUrlTemplate.replace('%%ADDRESS%%', address); // Replace the placeholder in the URL with the specified address
    }
    /**
     * Get the URL for exploring a specific transaction.
     *
     * @param {string} txID The transaction ID to be explored
     * @returns {string} The URL for exploring the specified transaction
     */
    getExplorerTxUrl(txID) {
        return this.explorerTxUrlTemplate.replace('%%TX_ID%%', txID); // Replace the placeholder in the URL with the specified transaction ID
    }
}

/**
 * Enum defining supported blockchain protocols.
 */
exports.Protocol = void 0;
(function (Protocol) {
    Protocol[Protocol["THORCHAIN"] = 1] = "THORCHAIN";
})(exports.Protocol || (exports.Protocol = {}));

exports.BaseXChainClient = BaseXChainClient;
exports.ExplorerProvider = ExplorerProvider;
exports.checkFeeBounds = checkFeeBounds;
exports.singleFee = singleFee;
exports.singleFeeRate = singleFeeRate;
exports.standardFeeRates = standardFeeRates;
exports.standardFees = standardFees;
//# sourceMappingURL=index.js.map
